import { useMediaQuery } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { ReactElement, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { LineType } from 'interfaces'
import { Config } from '../../../../../src/config/ConfigManager.ts'
import { BannerPromoDrawer } from '../../../../../src/features/search/Results/BannerPromoDrawer.tsx'
import { useUiStateStore } from '../../../../../src/store/uiState/UiStateStore.ts'
import { breakpoints } from '../../../../../src/theme/breakpoints.ts'
import { BannerAdsSearchResults } from '../../../../../src/ui-components/util/BannerAdsSearchResults.tsx'
import { useResultsStore } from '../../store/ResultsStore.ts'
import { StockOptions } from '../../constants.ts'
import BackToTopButton from './BackToTopButton.tsx'
import FitmentDisclaimer from './FitmentDisclaimer.tsx'
import NoResultsWrapper from './NoResultsWrapper.tsx'
import ResultsContentHeader from './ResultsContentHeader.tsx'
import SaytRecommendationsBox from './SaytRecommendationsBox.tsx'
import { FilterChipBar } from '../../../../../src/features/filterResults/components/FilterChipBar.tsx'

import { StoreInstances } from 'src/store/StoreInstancesContainer.ts'
import { SearchStatus } from '../../../../../src/features/search/Results/components/multiSeller/storeCard/Enums.ts'
import TableWrapper from './table/Wrapper.tsx'
import { UnableToConnect } from './UnableToConnect.tsx'
import InquiryTimeoutError from './InquiryTimeoutError'
import { useSearchStore } from '../../../../../src/store/search/SearchStore'
import { SortNotificationBanner } from './SortNotificationBanner.tsx'
interface Props {
  onClickFilters: () => void
}

export const ResultsContent = observer((props: Props): ReactElement => {
  const resultsStore = useResultsStore()
  const searchStore = useSearchStore()

  const { searchResults, filters, searchDiagramsByPartsBinId, sellerDetails } =
    resultsStore
  const { onClickFilters } = props
  const uiStore = useUiStateStore()

  const matches = useMediaQuery('(max-width:1460px)')

  const parts =
    searchResults?.parts?.filter((part) => part.type === LineType.part) ?? []

  useEffect(() => {
    if (resultsStore?.partsBinId === undefined) return
    searchDiagramsByPartsBinId(resultsStore?.partsBinId)
  }, [resultsStore?.partsBinId, searchDiagramsByPartsBinId])

  const { totalCount, filteredCount, isSnwFinished } = searchResults
  const totalResults = filteredCount ?? totalCount ?? 0
  const hasResultsToFetch =
    !!filteredCount && !!totalCount && filteredCount < totalCount

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollCountRef = useRef(0)
  const storeRef = useRef(resultsStore)
  storeRef.current = resultsStore

  // Always reflect latest values inside interval without re-creating it
  const snwRef = useRef(isSnwFinished)
  snwRef.current = isSnwFinished
  const hasResultsToFetchRef = useRef(hasResultsToFetch)
  hasResultsToFetchRef.current = hasResultsToFetch

  const stopPolling = useRef(() => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    intervalRef.current = null
    timeoutRef.current = null
    pollCountRef.current = 0
  }).current

  useEffect(() => {
    const selectedStockOption = storeRef.current.getDefaultStockFilterOption(
      StoreInstances?.userStore?.preferences?.display_filtering,
      StoreInstances?.userStore?.preferences?.display_userFiltering
    )

    const canPoll =
      hasResultsToFetchRef.current &&
      !isSnwFinished &&
      selectedStockOption !== StockOptions.SHOW_ALL &&
      storeRef.current.filters.length === 0

    if (!canPoll || intervalRef.current) return stopPolling

    pollCountRef.current = 0

    intervalRef.current = setInterval(() => {
      if (snwRef.current || pollCountRef.current >= 10) {
        stopPolling()
        return
      }
      pollCountRef.current++
      storeRef.current.searchByPartBin(0)
    }, 3000)

    timeoutRef.current = setTimeout(stopPolling, 30000)

    return stopPolling
    // Only re-evaluate whether to START polling when snwFinished changes.
    // hasResultsToFetch is read via ref inside the interval to avoid restarting
    // the interval on every searchByPartBin response.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSnwFinished])

  return (
    <Wrapper filtersCollapsed={matches}>
      <BannerAd>
        {import.meta.env.VITE_FEATURE_BANNER_ADS_SEARCH_RESULTS === 'true' && (
          <BannerAdsSearchResults />
        )}
      </BannerAd>
      {!searchStore.isLoading &&
        searchStore.saytRecommendations &&
        searchStore.saytRecommendations.length > 0 && (
          <SaytRecommendationsBox />
        )}
      <ResultsContentHeader
        totalResults={totalResults}
        onClickFilters={onClickFilters}
      />
      {!matches && <FilterChipBar />}
      {searchStore.showPartsNotFitVehicleLabel && totalResults > 0 && (
        <FitmentDisclaimer />
      )}
      {/* MPV3-9321 added new sort items notification ui component */}
      <SortNotificationBanner />
      {searchStore.inquiryTimeoutError && <InquiryTimeoutError />}

      {!resultsStore.isLoading &&
      parts.length === 0 &&
      !searchStore.inquiryTimeoutError &&
      sellerDetails?.searchStatus !== SearchStatus.ERROR ? (
        <NoResultsWrapper filters={filters} />
      ) : (
        <>
          {/* Removed isLoading check below because we still want to display results when the next page is loading */}
          {parts.length > 0 && (
            <>
              <BackToTopButton />
              <TableWrapper parts={parts} />
            </>
          )}
          {sellerDetails?.searchStatus === SearchStatus.ERROR && (
            <UnableToConnect showContactUs={!sellerDetails?.isMyPlaceStore} />
          )}
        </>
      )}
      {Config.promotionsEnabled && uiStore.isPromoEnabled() && (
        <BannerPromoDrawer />
      )}
    </Wrapper>
  )
})

ResultsContent.displayName = 'ResultsContent'

interface WrapperProps {
  filtersCollapsed: boolean
}

const BannerAd = styled.div`
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    display: none;
  }
`
const Wrapper = styled.div<WrapperProps>`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-width: 0;
  overflow-x: clip;
  margin-left: ${(p) => (p.filtersCollapsed ? '20px' : '10px')};
  margin-right: 20px;
  margin-bottom: 15px;

  @media screen and (width < ${breakpoints.tabletLarge}px) {
    margin-left: 16px;
    margin-right: 16px;
  }
`
